/**
 * Onboarding Page
 * Free Tier Hotel Application Form (Max 25 rooms)
 * Integrated with LINE OA / LIFF (@metagoldtech Gateway)
 */

import { getState, setLoading, setError } from '../store';
import { getLanguage, t } from '../i18n';
import { getLocalizedProvinces } from '../data/provinces';
import { getTenantId, getApiBaseUrl } from '../config';
import { Header, AppVersionFooter } from '../components';
import { closeWindow } from '../liff';

interface FreeTierStatus {
  activeCount: number;
  maxActive: number;
  slotsRemaining: number;
  waitingListCount: number;
  isQuotaFull: boolean;
}

let cachedStatus: FreeTierStatus | null = null;
let submitResult: any = null;

export async function fetchFreeTierStatus(): Promise<FreeTierStatus> {
  try {
    const apiBase = getApiBaseUrl();
    const res = await fetch(`${apiBase}/api/v1/onboarding/free-tier-status`);
    const json = await res.json();
    if (json.success && json.data) {
      cachedStatus = json.data;
      return json.data;
    }
  } catch (err) {
    console.warn('[OnboardPage] Failed to fetch free tier status:', err);
  }
  return {
    activeCount: 0,
    maxActive: 50,
    slotsRemaining: 50,
    waitingListCount: 0,
    isQuotaFull: false,
  };
}

export function renderOnboardPage(): string {
  const user = getState().user;
  const rawDisplayName = user?.displayName || '';
  const safeDisplayName =
    rawDisplayName &&
    rawDisplayName !== 'LINE Guest' &&
    rawDisplayName !== 'Guest' &&
    !rawDisplayName.startsWith('Room ') &&
    !rawDisplayName.startsWith('line_guest')
      ? rawDisplayName
      : '';
  const currentLang = getLanguage();
  const provincesList = getLocalizedProvinces(currentLang);
  const status = cachedStatus || {
    activeCount: 0,
    maxActive: 50,
    slotsRemaining: 50,
    waitingListCount: 0,
    isQuotaFull: false,
  };

  if (submitResult) {
    const isGranted = submitResult.status === 'active';
    const isPending = submitResult.status === 'pending_approval';
    const isWaiting = submitResult.status === 'waiting_list';

    const resultIcon = isGranted ? '🎉' : isPending ? '📋' : '⏳';
    const resultTitle = isGranted
      ? t('onboardSuccessTitle')
      : isPending
      ? t('onboardPendingTitle')
      : t('onboardWaitingTitle');

    return `
      ${Header(t('onboardTitle'), false)}
      <div class="page-content onboard-page">
        <div class="onboard-result-card">
          <div class="onboard-result-icon ${isGranted ? 'is-active' : isPending ? 'is-pending' : 'is-waiting'}">
            ${resultIcon}
          </div>
          <h2 style="font-size: 20px; font-weight: 700; color: #1f2937; margin-bottom: 8px;">
            ${resultTitle}
          </h2>
          <p style="font-size: 14px; color: #4b5563; line-height: 1.6; margin-bottom: 16px;">
            ${submitResult.message}
          </p>

          ${isGranted ? `
            <div style="background: #f5f3ff; border: 1px solid #ddd6fe; border-radius: 14px; padding: 14px 16px; text-align: left; margin-bottom: 16px; font-size: 13px; color: #5b21b6; line-height: 1.6;">
              <div style="font-weight: 700; margin-bottom: 4px;">🔐 ${t('onboardCredentialsSentNotice')}</div>
              <div style="font-size: 12px; color: #6d28d9;">กรุณาตรวจสอบข้อความจาก LINE OA @metagoldtech และอีเมลของท่าน เพื่อรับ Username และ Password เข้าสู่หน้า Admin</div>
            </div>
          ` : isPending ? `
            <div style="background: #fffbeb; border: 1px solid #fde68a; border-radius: 14px; padding: 14px 16px; text-align: left; margin-bottom: 16px; font-size: 13px; color: #92400e; line-height: 1.6;">
              <div style="font-weight: 700; margin-bottom: 4px;">📋 ${t('onboardPendingTitle')}</div>
              <div style="font-size: 12px; color: #b45309;">${t('onboardPendingNotice')}</div>
            </div>
          ` : ''}

          ${isWaiting && submitResult.queueNumber ? `
            <div style="background: #fffbeb; border: 1px solid #fde68a; border-radius: 14px; padding: 16px; margin-bottom: 20px;">
              <div style="font-size: 11px; font-weight: 700; color: #b45309; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">
                ${t('onboardQueueNumberLabel')}
              </div>
              <div style="font-size: 32px; font-weight: 800; color: #78350f;">
                #${submitResult.queueNumber}
              </div>
              <div style="font-size: 12px; color: #92400e; margin-top: 6px;">
                ${submitResult.estimatedActivation}
              </div>
            </div>
          ` : `
            <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 14px; padding: 14px 16px; text-align: left; margin-bottom: 20px; font-size: 13px; color: #166534; line-height: 1.8;">
              <div style="font-weight: 700; margin-bottom: 4px;">✨ ระบบที่จะเปิดใช้งานหลังอนุมัติ:</div>
              <div>✓ ${t('onboardFeatureStayTitle')}</div>
              <div>✓ ${t('onboardFeatureDiningTitle')}</div>
              <div>✓ ${t('onboardFeatureSpaTitle')}</div>
              <div>✓ ${t('onboardFeatureMiceTitle')}</div>
              <div>✓ ${t('onboardFeatureAiTitle')}</div>
            </div>
          `}

          <div style="background: #f3f4f6; border-radius: 10px; padding: 10px; font-size: 12px; color: #6b7280; font-family: monospace; margin-bottom: 16px;">
            Tenant ID: <strong style="color: #111827;">${submitResult.tenantId}</strong>
          </div>

          <div style="font-size: 12px; color: #6b7280; margin-bottom: 14px; font-weight: 500;">
            ⏳ ${t('onboardClosingWindow')}
          </div>

          <button
            id="onboard-close-btn"
            class="btn btn-primary btn-block btn-lg"
            style="border-radius: 14px;"
          >
            ${t('onboardCloseNowButton')}
          </button>
        </div>
        ${AppVersionFooter()}
      </div>
    `;
  }

  const badgeHtml = status.isQuotaFull
    ? `<div class="onboard-badge-pill is-full">⚠️ ${t('onboardQuotaFullBadge')}</div>`
    : `<div class="onboard-badge-pill">✨ ${t('onboardQuotaActiveBadge').replace('{slots}', String(status.slotsRemaining)).replace('{max}', String(status.maxActive))}</div>`;

  return `
    ${Header(t('onboardTitle'), false)}

    <div class="page-content onboard-page">
      <!-- Hero Banner -->
      <div class="onboard-hero-card">
        <div class="onboard-hero-logo">R</div>
        <h1 class="onboard-hero-title">${t('onboardTitle')}</h1>
        <p class="onboard-hero-desc">${t('onboardSubtitle')}</p>
        <div>${badgeHtml}</div>
      </div>

      <!-- Feature Matrix Card -->
      <div class="onboard-features-card">
        <div class="onboard-features-heading">
          <span>🎁</span> สิทธิประโยชน์ฟรี 100% (ไม่มีค่าใช้จ่ายแอบแฝง)
        </div>
        <ul class="onboard-features-list">
          <li class="onboard-feature-item">
            <span class="onboard-feature-icon">🏨</span>
            <span>${t('onboardFeatureStayTitle')}</span>
          </li>
          <li class="onboard-feature-item">
            <span class="onboard-feature-icon">🍽️</span>
            <span>${t('onboardFeatureDiningTitle')}</span>
          </li>
          <li class="onboard-feature-item">
            <span class="onboard-feature-icon">💆</span>
            <span>${t('onboardFeatureSpaTitle')}</span>
          </li>
          <li class="onboard-feature-item">
            <span class="onboard-feature-icon">📑</span>
            <span>${t('onboardFeatureMiceTitle')}</span>
          </li>
          <li class="onboard-feature-item">
            <span class="onboard-feature-icon">🤖</span>
            <span>${t('onboardFeatureAiTitle')}</span>
          </li>
        </ul>
      </div>

      <!-- Application Form -->
      <div class="onboard-form-card">
        <form id="onboard-form">
          <div class="onboard-section-title">${t('onboardSectionHotel')}</div>

          <div class="form-group">
            <label class="form-label" for="hotel-name">
              ${t('onboardHotelNameLabel')} <span class="required">*</span>
            </label>
            <input
              type="text"
              id="hotel-name"
              name="hotel-name"
              class="form-input"
              required
              placeholder="${t('onboardHotelNamePlaceholder')}"
            />
          </div>

          <!-- Room Count Stepper -->
          <div class="form-group">
            <label class="form-label" for="room-count">
              ${t('onboardRoomCountLabel')} <span class="required">*</span>
            </label>
            <div style="display: flex; align-items: center; gap: 8px;">
              <button
                type="button"
                id="btn-room-decrease"
                class="stepper-btn"
                style="width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 8px; font-size: 22px; font-weight: bold; color: #374151; cursor: pointer; user-select: none;"
                aria-label="Decrease room count"
              >−</button>
              <input
                type="number"
                id="room-count"
                name="room-count"
                class="form-input"
                min="1"
                max="25"
                value="20"
                required
                style="text-align: center; font-size: 18px; font-weight: 700; width: 80px; height: 44px; padding: 0 4px;"
              />
              <button
                type="button"
                id="btn-room-increase"
                class="stepper-btn"
                style="width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 8px; font-size: 22px; font-weight: bold; color: #374151; cursor: pointer; user-select: none;"
                aria-label="Increase room count"
              >+</button>
              <span style="font-size: 14px; color: #4b5563; font-weight: 500; margin-left: 4px;">${t('rooms')}</span>
            </div>
            <div style="font-size: 11px; color: #6b7280; margin-top: 6px;">
              ℹ️ ${t('onboardRoomCountHelp')}
            </div>
          </div>

          <!-- Province Select Dropdown -->
          <div class="form-group">
            <label class="form-label" for="province">
              ${t('onboardProvinceLabel')}
            </label>
            <select
              id="province"
              name="province"
              class="form-select"
              style="width: 100%; height: 44px; font-size: 15px; border-radius: 8px; border: 1px solid #d1d5db; background: white; padding: 0 12px;"
            >
              <option value="">${t('onboardSelectProvincePlaceholder')}</option>
              ${provincesList.map((p) => `<option value="${p.value}">${p.label}</option>`).join('')}
              <option value="other">${t('onboardProvinceOther')}</option>
            </select>
            <div id="province-other-wrapper" style="display: none; margin-top: 8px;">
              <input
                type="text"
                id="province-other-input"
                class="form-input"
                placeholder="${t('onboardProvincePlaceholder')}"
                style="height: 42px;"
              />
            </div>
          </div>

          <div class="onboard-section-title">${t('onboardSectionAdmin')}</div>

          <div class="form-group">
            <label class="form-label" for="contact-person">
              ${t('onboardContactPersonLabel')} <span class="required">*</span>
            </label>
            <input
              type="text"
              id="contact-person"
              name="contact-person"
              class="form-input"
              required
              value="${safeDisplayName}"
              placeholder="${t('onboardContactPersonPlaceholder')}"
            />
          </div>

          <div class="form-group">
            <label class="form-label" for="contact-phone">
              ${t('onboardContactPhoneLabel')} <span class="required">*</span>
            </label>
            <input
              type="tel"
              id="contact-phone"
              name="contact-phone"
              class="form-input"
              required
              placeholder="${t('onboardContactPhonePlaceholder')}"
            />
          </div>

          <div class="form-group">
            <label class="form-label" for="contact-email">
              ${t('onboardContactEmailLabel')} <span class="required">*</span>
            </label>
            <input
              type="email"
              id="contact-email"
              name="contact-email"
              class="form-input"
              required
              placeholder="${t('onboardContactEmailPlaceholder')}"
            />
          </div>

          <!-- Terms & Conditions Affirmative Checkbox -->
          <div class="onboard-terms-card" style="margin: 16px 0 12px; padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px;">
            <label style="display: flex; align-items: flex-start; gap: 10px; cursor: pointer; user-select: none;">
              <input
                type="checkbox"
                id="accept-terms"
                name="accept-terms"
                style="width: 20px; height: 20px; margin-top: 2px; accent-color: #6366f1; cursor: pointer; flex-shrink: 0;"
                required
              />
              <span style="font-size: 13px; line-height: 1.5; color: #334155;">
                ${t('onboardTermsLabel')}
                <br>
                <a href="javascript:void(0)" id="btn-open-terms-modal" style="color: #4f46e5; font-weight: 600; text-decoration: underline; display: inline-block; margin-top: 4px;">
                  📄 ${t('onboardTermsReadLink')}
                </a>
              </span>
            </label>
          </div>

          <div id="form-error" style="display: none; padding: 10px 14px; background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; border-radius: 10px; font-size: 13px; margin: 12px 0;"></div>

          <button
            type="submit"
            id="submit-btn"
            class="onboard-submit-btn"
          >
            <span>📝</span> <span>${t('onboardSubmitButton')}</span>
          </button>
        </form>
      </div>

      <!-- Terms & Conditions Full Modal -->
      <div id="terms-modal" style="display: none; position: fixed; inset: 0; background: rgba(15, 23, 42, 0.65); backdrop-filter: blur(4px); z-index: 9999; align-items: center; justify-content: center; padding: 16px;">
        <div style="background: white; border-radius: 20px; max-width: 520px; width: 100%; max-height: 85vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);">
          <!-- Header -->
          <div style="padding: 16px 20px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between;">
            <div style="font-size: 16px; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 8px;">
              <span>📜</span> <span>${t('onboardTermsModalTitle')}</span>
            </div>
            <button type="button" id="btn-close-terms-modal-x" style="background: transparent; border: none; font-size: 22px; line-height: 1; color: #64748b; cursor: pointer; padding: 4px;">&times;</button>
          </div>
          <!-- Body -->
          <div style="padding: 20px; overflow-y: auto; font-size: 13px; line-height: 1.7; color: #334155; display: flex; flex-direction: column; gap: 14px;">
            <div style="padding: 12px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 12px; color: #166534; font-weight: 500;">
              ✨ Revio Free Tier เป็นโครงการส่งเสริมการนำเทคโนโลยีมาขับเคลื่อนธุรกิจ (Hotel & Commerce Digital Transformation) โดย Meta Gold Technology Co., Ltd.
            </div>
            <div style="display: flex; flex-direction: column; gap: 10px;">
              <div style="display: flex; gap: 8px; align-items: flex-start;">
                <span>🏨</span>
                <div><strong>1. ${t('onboardTermsSummaryScope')}</strong></div>
              </div>
              <div style="display: flex; gap: 8px; align-items: flex-start;">
                <span>⏳</span>
                <div><strong>2. ${t('onboardTermsSummaryInactivity')}</strong></div>
              </div>
              <div style="display: flex; gap: 8px; align-items: flex-start;">
                <span>💳</span>
                <div><strong>3. ${t('onboardTermsSummaryNoFee')}</strong></div>
              </div>
              <div style="display: flex; gap: 8px; align-items: flex-start;">
                <span>🔒</span>
                <div><strong>4. ${t('onboardTermsSummaryDataPrivacy')}</strong></div>
              </div>
              <div style="display: flex; gap: 8px; align-items: flex-start;">
                <span>🛡️</span>
                <div><strong>5. ${t('onboardTermsSummaryAsIs')}</strong></div>
              </div>
            </div>
          </div>
          <!-- Footer -->
          <div style="padding: 16px 20px; border-top: 1px solid #e2e8f0; display: flex; gap: 10px; justify-content: flex-end; background: #f8fafc;">
            <button type="button" id="btn-close-terms-modal" style="padding: 10px 16px; border: 1px solid #cbd5e1; background: white; border-radius: 10px; font-size: 13px; font-weight: 600; color: #475569; cursor: pointer;">
              ${t('onboardTermsModalClose')}
            </button>
            <button type="button" id="btn-accept-terms-modal" style="padding: 10px 18px; border: none; background: #4f46e5; border-radius: 10px; font-size: 13px; font-weight: 700; color: white; cursor: pointer;">
              ✓ ${t('onboardTermsModalAccept')}
            </button>
          </div>
        </div>
      </div>

      ${AppVersionFooter()}
    </div>
  `;
}

export function initOnboardPage(): void {
  const closeBtn = document.getElementById('onboard-close-btn');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      closeWindow();
    });
  }

  const form = document.getElementById('onboard-form') as HTMLFormElement;
  const errorBox = document.getElementById('form-error');
  const submitBtn = document.getElementById('submit-btn') as HTMLButtonElement;

  // Stepper handlers for Room Count
  const roomInput = document.getElementById('room-count') as HTMLInputElement;
  const decBtn = document.getElementById('btn-room-decrease');
  const incBtn = document.getElementById('btn-room-increase');

  if (roomInput) {
    decBtn?.addEventListener('click', () => {
      let val = parseInt(roomInput.value, 10);
      if (isNaN(val)) val = 20;
      if (val > 1) {
        roomInput.value = String(val - 1);
      }
    });

    incBtn?.addEventListener('click', () => {
      let val = parseInt(roomInput.value, 10);
      if (isNaN(val)) val = 20;
      if (val < 25) {
        roomInput.value = String(val + 1);
      }
    });

    roomInput.addEventListener('change', () => {
      let val = parseInt(roomInput.value, 10);
      if (isNaN(val) || val < 1) val = 1;
      if (val > 25) val = 25;
      roomInput.value = String(val);
    });
  }

  // Province select handler (toggle custom input if "other" is selected)
  const provinceSelect = document.getElementById('province') as HTMLSelectElement;
  const provinceOtherWrapper = document.getElementById('province-other-wrapper');
  const provinceOtherInput = document.getElementById('province-other-input') as HTMLInputElement;

  if (provinceSelect && provinceOtherWrapper) {
    provinceSelect.addEventListener('change', () => {
      if (provinceSelect.value === 'other') {
        provinceOtherWrapper.style.display = 'block';
        provinceOtherInput?.focus();
      } else {
        provinceOtherWrapper.style.display = 'none';
      }
    });
  }

  // Terms & Conditions Modal handlers
  const termsModal = document.getElementById('terms-modal');
  const btnOpenTerms = document.getElementById('btn-open-terms-modal');
  const btnCloseTermsX = document.getElementById('btn-close-terms-modal-x');
  const btnCloseTerms = document.getElementById('btn-close-terms-modal');
  const btnAcceptTermsModal = document.getElementById('btn-accept-terms-modal');
  const checkboxTerms = document.getElementById('accept-terms') as HTMLInputElement;

  const showTermsModal = () => {
    if (termsModal) {
      termsModal.style.display = 'flex';
    }
  };
  const hideTermsModal = () => {
    if (termsModal) {
      termsModal.style.display = 'none';
    }
  };

  btnOpenTerms?.addEventListener('click', showTermsModal);
  btnCloseTermsX?.addEventListener('click', hideTermsModal);
  btnCloseTerms?.addEventListener('click', hideTermsModal);
  btnAcceptTermsModal?.addEventListener('click', () => {
    if (checkboxTerms) {
      checkboxTerms.checked = true;
    }
    hideTermsModal();
  });

  if (!form) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const hotelName = (document.getElementById('hotel-name') as HTMLInputElement)?.value?.trim();
    const roomCount = parseInt(roomInput?.value || '20', 10);
    let province = provinceSelect?.value?.trim() || '';
    if (province === 'other') {
      const otherVal = provinceOtherInput?.value?.trim();
      province = otherVal || t('onboardProvinceOther');
    }
    const contactPerson = (document.getElementById('contact-person') as HTMLInputElement)?.value?.trim();
    const contactPhone = (document.getElementById('contact-phone') as HTMLInputElement)?.value?.trim();
    const contactEmail = (document.getElementById('contact-email') as HTMLInputElement)?.value?.trim();
    const isTermsAccepted = checkboxTerms ? checkboxTerms.checked : false;

    if (!hotelName || !contactPerson || !contactPhone || !contactEmail) {
      if (errorBox) {
        errorBox.textContent = t('onboardRequiredFieldsError');
        errorBox.style.display = 'block';
        errorBox.classList.remove('hidden');
        errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!isTermsAccepted) {
      if (errorBox) {
        errorBox.textContent = t('onboardTermsRequiredError');
        errorBox.style.display = 'block';
        errorBox.classList.remove('hidden');
        errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (roomCount > 25) {
      if (errorBox) {
        errorBox.textContent = t('onboardMaxRoomsError');
        errorBox.style.display = 'block';
        errorBox.classList.remove('hidden');
        errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = `<span>⏳</span> <span>${t('onboardSubmitting')}</span>`;
    }
    if (errorBox) {
      errorBox.style.display = 'none';
      errorBox.classList.add('hidden');
    }

    try {
      const user = getState().user;
      const gatewayTenantId = getTenantId() || 'metagoldtech';

      const payload = {
        gatewayTenantId,
        hotelName,
        contactPerson,
        contactPhone,
        contactEmail,
        roomCount,
        province,
        lineUserId: user?.userId?.startsWith('line_') || user?.userId?.startsWith('U') ? user.userId : undefined,
        lineDisplayName: user?.displayName,
        acceptTerms: true,
      };

      const apiBase = getApiBaseUrl();
      const res = await fetch(`${apiBase}/api/v1/onboarding/free-tier-apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Submission failed');
      }

      submitResult = data;
      // Re-render onboard page to show result card
      const app = document.getElementById('app');
      if (app) {
        app.innerHTML = renderOnboardPage();
        initOnboardPage();
      }

      // Auto-close window after 1.2s to smoothly return to LINE chat
      setTimeout(() => {
        closeWindow();
      }, 1200);
    } catch (err: any) {
      console.error('[OnboardPage] Submit error:', err);
      if (errorBox) {
        errorBox.textContent = err.message || 'เกิดข้อผิดพลาดในการส่งข้อมูล';
        errorBox.style.display = 'block';
        errorBox.classList.remove('hidden');
        errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = `<span>${t('onboardSubmitButton')}</span>`;
      }
    }
  });
}
